import { AdDismissedError, AdTooSoonError, AdUnavailableError } from './ads.js';

/**
 * GigaPub's SDK (docs.giga.pub), for the task centre's ad slot.
 *
 * Its script defines `window.showGiga()`, which plays an ad over the app and
 * resolves once one was shown and watched (true), or false when only a
 * fallback ran. It rejects when it found no ad, when it is called again while
 * one is showing, and when an ad was closed too soon. It reads the player from
 * Telegram's WebApp itself.
 *
 * The script is loaded a few seconds after the app opens (app/App.tsx,
 * warmGigapub), so the networks GigaPub sells for are in hand before the
 * slot asks for an ad; when that did not happen, the slot's tap loads it.
 * From ad.gigapub.tech, then from GigaPub's backup server when that one fails
 * or takes over 15 seconds — the order GigaPub's own "enhanced reliability"
 * loader tries them in.
 */

declare global {
  interface Window {
    showGiga?: (options?: unknown) => Promise<unknown>;
  }
}

const SERVERS = ['https://ad.gigapub.tech', 'https://ru-ad.gigapub.tech'];
const LOAD_TIMEOUT_MS = 15_000;
/** How long `showGiga` may take to appear once the script has run. */
const READY_TIMEOUT_MS = 4_000;

let loading: { projectId: string; promise: Promise<void> } | null = null;

function inject(src: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    const timer = window.setTimeout(() => {
      script.remove();
      reject(new Error('GigaPub’s script took too long.'));
    }, LOAD_TIMEOUT_MS);
    script.onload = () => {
      window.clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      script.remove();
      reject(new Error('GigaPub’s script could not be loaded.'));
    };
    document.head.appendChild(script);
  });
}

async function ready(): Promise<void> {
  const until = Date.now() + READY_TIMEOUT_MS;
  while (typeof window.showGiga !== 'function') {
    if (Date.now() > until) throw new Error('GigaPub’s script did not start.');
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

/** GigaPub's script for the project, once per page; rejects with AdUnavailableError when neither server answers. */
export function loadGigapub(projectId: string): Promise<void> {
  if (typeof window.showGiga === 'function') return Promise.resolve();
  if (loading && loading.projectId === projectId) return loading.promise;
  const promise = (async () => {
    for (const server of SERVERS) {
      try {
        await inject(`${server}/script?id=${encodeURIComponent(projectId)}`);
        await ready();
        return;
      } catch {
        // The next server, if any.
      }
    }
    throw new AdUnavailableError('GigaPub could not be reached.', 'unreachable');
  })();
  loading = { projectId, promise };
  promise.catch(() => {
    if (loading?.promise === promise) loading = null;
  });
  return promise;
}

/**
 * Plays one GigaPub ad, from the placement the panel names — the list of
 * networks GigaPub set up for the project under that name; none named, its
 * default. Resolves once it was watched; rejects with the app's own ad errors,
 * which say why in the reader's language: no ad to show, one already showing,
 * or closed too soon.
 */
export async function playGigapub(placement = ''): Promise<void> {
  const show = window.showGiga;
  if (typeof show !== 'function') throw new AdUnavailableError('GigaPub did not load.', 'notLoaded');
  let watched: unknown;
  try {
    watched = await (placement ? show(placement) : show());
  } catch (error) {
    const text = `${(error as { name?: unknown })?.name ?? ''} ${(error as { message?: unknown })?.message ?? error}`;
    if (/already showing/i.test(text)) throw new AdTooSoonError();
    if (/too ?fast/i.test(text)) throw new AdDismissedError();
    throw new AdUnavailableError('No GigaPub ad to show right now.', 'noFill');
  }
  // false: no network paid for it, only a fallback ran.
  if (watched === false) throw new AdUnavailableError('No GigaPub ad to show right now.', 'noFill');
}
